import React, { useState } from 'react';
import { FieldArray, FormikProvider, getIn } from 'formik';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import DeleteIcon from '@mui/icons-material/Delete';
import NumberFormatCustom from '../common/form/NumberFormatCustom';
import PartnerModalConatiner from '../../containers/common/form/modal/PartnerModalConatiner';

/**
 * 제품 매입가 입력(매입처별 리스트). "가격타입" 자유선택(매입 가격/소비자 가격/고객사
 * 가격/파트너 가격/프리미엄 리셀러 가격)을 없애고, 매입처마다 매입가 1행씩 등록하는
 * 구조로 교체(2026-10-27, 제품 가격타입 제거). 매입처는 필수, 제품당 여러 매입처 허용.
 */
const ProductPurchasePrices = ({ formik }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const purchasePrices = formik.values.purchasePrices || [];

  return (
    // FieldArray(formik v2)는 FormikContext를 통해 동작한다. ProductAddForm/ProductForm은
    // <FormikProvider>로 감싸지 않고 formik 객체를 prop으로 바로 내려주므로, 여기서 직접
    // Provider를 열어 컨텍스트를 만들어준다(없으면 "Formik context is undefined" 경고와
    // 함께 push/remove가 동작하지 않는다).
    <FormikProvider value={formik}>
    <FieldArray
      name="purchasePrices"
      render={(arrayHelpers) => (
        <Box>
          {purchasePrices.length === 0 ? (
            <Typography variant="body2" sx={{ py: 1, color: 'text.secondary' }}>
              매입처별 매입가를 1건 이상 등록하세요.
            </Typography>
          ) : (
            <TableContainer component={Box}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="10%">No</TableCell>
                    <TableCell width="40%">매입처</TableCell>
                    <TableCell width="35%">매입가</TableCell>
                    <TableCell width="15%">삭제</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchasePrices.map((row, index) => {
                    const partnerNmPath = `purchasePrices[${index}].partnerNm`;
                    const priceValPath = `purchasePrices[${index}].priceVal`;
                    const errorPartner = getIn(formik.errors, `purchasePrices[${index}].partnerUuid`);
                    const touchedPartner = getIn(formik.touched, `purchasePrices[${index}].partnerUuid`);
                    const errorPrice = getIn(formik.errors, priceValPath);
                    const touchedPrice = getIn(formik.touched, priceValPath);

                    return (
                      <TableRow key={index}>
                        <TableCell align="center">{index + 1}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              size="small"
                              variant="standard"
                              fullWidth
                              disabled
                              value={getIn(formik.values, partnerNmPath) || ''}
                              error={Boolean(touchedPartner && errorPartner)}
                              helperText={touchedPartner && errorPartner ? errorPartner : ''}
                            />
                            <Button
                              size="small"
                              color="secondary"
                              variant="outlined"
                              onClick={() => setOpenIndex(index)}
                            >
                              선택
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="standard"
                            fullWidth
                            value={getIn(formik.values, priceValPath) || ''}
                            onChange={(e) =>
                              formik.setFieldValue(priceValPath, e.target.value)
                            }
                            onBlur={formik.handleBlur}
                            error={Boolean(touchedPrice && errorPrice)}
                            helperText={touchedPrice && errorPrice ? errorPrice : ''}
                            InputProps={{ inputComponent: NumberFormatCustom }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            aria-label="delete"
                            size="small"
                            onClick={() => arrayHelpers.remove(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                        {openIndex === index && (
                          <PartnerModalConatiner
                            link
                            title="매입처 선택"
                            open={openIndex === index}
                            handleClose={() => setOpenIndex(null)}
                            selectedPartner={(partner) => {
                              arrayHelpers.replace(index, {
                                ...purchasePrices[index],
                                partnerUuid: partner.partnerUuid,
                                partnerNm: partner.partnerNm,
                              });
                              setOpenIndex(null);
                            }}
                          />
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 1 }}>
            <Button
              size="small"
              color="secondary"
              variant="outlined"
              onClick={() =>
                arrayHelpers.push({ partnerUuid: '', partnerNm: '', priceVal: '' })
              }
            >
              매입처 추가
            </Button>
          </Box>
        </Box>
      )}
    />
    </FormikProvider>
  );
};

export default ProductPurchasePrices;
